'use client';

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData } from '@tanstack/react-query';
import { z } from 'zod';
import { AnyObject } from '@sola/schema/dotcom/helpers.ts';
import { useListSearchResultsQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import { trackError } from '@sola/tracking/components/datalayer.tsx';
const SearchResultsSchema = z.object({
  q: z.string().optional(),
  page: z.coerce.number().int().nonnegative().catch(0).optional()
});
type SearchResultsSchema = z.infer<typeof SearchResultsSchema>;
export const parseUrlParams = ({
  searchParams,
  schema
}: {
  searchParams: URLSearchParams;
  schema: z.ZodSchema;
}) => {
  const plainSearchParams = Object.fromEntries(searchParams.entries());
  try {
    const validatedParams = schema.parse(plainSearchParams);
    return new URLSearchParams(validatedParams as AnyObject);
  } catch (error) {
    if (error instanceof z.ZodError) {
      trackError(error.message);
      console.error('Validation error:', error.issues);
    } else {
      trackError(error as string);
      console.error('Unexpected error:', error);
    }
  }
};
export const getLegacyQuery = () => {
  if (typeof window !== 'undefined' && window.location.hash) {
    return window.location.hash.replace(/^#/, '').split('&').find(segment => segment.startsWith('stq='))?.replace('stq=', '');
  }
};
export const useSearchAPI = (searchQuery, itemsPerPage, searchParams) => {
  const $ = _c(10);
  const t0 = searchParams.page ? searchParams.page * itemsPerPage : 0;
  let t1;
  if ($[0] !== itemsPerPage || $[1] !== searchQuery || $[2] !== t0) {
    t1 = {
      perPage: itemsPerPage,
      offset: t0,
      query: searchQuery
    };
    $[0] = itemsPerPage;
    $[1] = searchQuery;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!searchQuery;
  let t3;
  if ($[4] !== t2) {
    t3 = {
      enabled: t2,
      placeholderData: keepPreviousData
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data,
    error,
    isLoading
  } = useListSearchResultsQuery(t1, t3);
  const t4 = isLoading && !!searchQuery;
  let t5;
  if ($[6] !== data || $[7] !== error || $[8] !== t4) {
    t5 = {
      list: data,
      queryError: error,
      isLoading: t4
    };
    $[6] = data;
    $[7] = error;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};